<template>
  <Transition name="chat-intro">
    <div v-show="visible" class="chat-intro">
      <div class="chat-intro-greeting">{{ welcomeGreeting }}</div>
      <div class="chat-intro-description">{{ description }}</div>
      <!--
        The number of prompts can grow or shrink at any point - on the initial fetch, and again
        whenever navigating to a page whose suggestions differ in count from the current one.
        Resizing this wrapper to the buttons' actual measured height (see measuredHeight) rather
        than letting it size to content directly is what lets that resize transition smoothly
        instead of snapping, which in turn is what lets the greeting and description above ease
        into their new position rather than jumping.
      -->
      <div class="suggested-prompts-grow" :style="{ height: `${measuredHeight}px` }">
        <!--
          Prompts are staggered in individually rather than appearing all at once.
          Each button's delay is set from its index.
        -->
        <TransitionGroup
          ref="promptsListRef"
          name="suggested-prompt"
          tag="div"
          class="suggested-prompts"
          @leave="onPromptLeave"
          @after-leave="onPromptAfterLeave"
        >
          <button
            v-for="(suggestion, index) in suggestions"
            :key="suggestion.name"
            type="button"
            class="suggested-prompt"
            :style="{ '--enter-delay': `${index * 70}ms` }"
            @click="$emit('select', suggestion)"
          >
            {{ suggestion.text }}
          </button>
        </TransitionGroup>
      </div>
    </div>
  </Transition>
</template>

<script>
// Exported so whatever renders this component can inject the styles where it actually ends
// up in the DOM. Scoped styles are no use when the host renders it inside a shadow root.
export const suggestedPromptsStyle = `
  .chat-intro {
    width: 100%;
    padding: 0 24px;
    box-sizing: border-box;
    color: var(--chatbot-text-color);
    text-align: center;
    /*
      Flex (rather than the default block flow) is what makes the padding/margin pairing on
      suggested-prompts-grow below cancel out cleanly - block layout would collapse that
      negative margin into chat-intro-description's margin-bottom instead.
    */
    display: flex;
    flex-direction: column;
  }
  .chat-intro-greeting {
    font-size: 20px;
    font-weight: 600;
  }
  .chat-intro-description {
    color: var(--chatbot-text-color-muted);
    margin: 8px 0 20px 0;
  }
  /*
    Deep Chat centers this whole component vertically within the message area based on its
    current height. Without this explicit height (kept in sync with the buttons' real height by
    measuredHeight/ResizeObserver), the prompts would resize to fit their content the instant it
    changes, so that re-centering would happen as an abrupt jump. Transitioning between explicit
    pixel heights instead means Deep Chat's centering math is recalculated smoothly on every
    frame, so the greeting and description above ease into their new position rather than
    snapping - both on the initial fetch and on any later change in how many prompts there are.

    overflow: hidden is what makes a height in progress actually look like it's still growing or
    shrinking, rather than just overlapping content below it - but it also clips anything that
    renders outside a button's own box, namely its drop shadow. The padding below gives the
    shadows room to render without being clipped, and the equal-but-opposite margin cancels the
    padding back out so it doesn't otherwise change this element's size or position - those
    particular pixel amounts come from the largest --chatbot-bubble-shadow any theme in
    chatbotThemes.js sets, which is the dark theme's.
  */
  .suggested-prompts-grow {
    overflow: hidden;
    padding: 8px 14px 20px 14px;
    margin: -8px -14px -20px -14px;
    transition: height 450ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .suggested-prompts {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .suggested-prompt {
    background: var(--chatbot-surface-background);
    color: var(--chatbot-text-color);
    border: var(--chatbot-border-width) solid var(--chatbot-surface-border-color);
    border-radius: 10px;
    box-shadow: var(--chatbot-bubble-shadow);
    padding: 12px 16px;
    font-size: inherit;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
  }
  .suggested-prompt:hover {
    background: var(--chatbot-highlight-background);
    border-color: var(--chatbot-highlight-border-color);
  }
  .suggested-prompt:focus-visible {
    outline: 2px solid var(--chatbot-focus-ring-color);
    outline-offset: 2px;
  }
  .suggested-prompt:active {
    background: var(--chatbot-prompt-active-background);
  }

  /* The intro fades and slides up as a block once the chat is known to be empty. */
  .chat-intro-enter-active,
  .chat-intro-leave-active {
    transition:
      opacity 400ms ease,
      transform 400ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .chat-intro-enter-from,
  .chat-intro-leave-to {
    opacity: 0;
    transform: translateY(16px);
  }

  /* Prompts that arrive later cascade in on top of that, one after another. */
  .suggested-prompt-enter-active {
    transition:
      opacity 350ms ease,
      transform 350ms cubic-bezier(0.22, 1, 0.36, 1);
    transition-delay: var(--enter-delay, 0ms);
  }
  .suggested-prompt-enter-from {
    opacity: 0;
    transform: translateY(12px);
  }

  /*
    Leaving mirrors entering: the same fade and slide distance, run in reverse. The
    transform easing is entering's cubic-bezier reflected through time (an ease-out curve
    becomes ease-in), so a prompt eases out the way it eased in rather than departing on an
    unrelated curve. No stagger - only navigating away replaces the list, so there's no
    "cascading in" reason to stagger the exit too.
  */
  .suggested-prompt-leave-active {
    transition:
      opacity 350ms ease,
      transform 350ms cubic-bezier(0.64, 0, 0.78, 0);
  }
  .suggested-prompt-leave-to {
    opacity: 0;
    transform: translateY(12px);
  }

  @media (prefers-reduced-motion: reduce) {
    .chat-intro-enter-active,
    .chat-intro-leave-active,
    .suggested-prompt-enter-active,
    .suggested-prompt-leave-active,
    .suggested-prompts-grow {
      transition-duration: 1ms;
      transition-delay: 0ms;
    }
    .chat-intro-enter-from,
    .chat-intro-leave-to,
    .suggested-prompt-enter-from,
    .suggested-prompt-leave-to {
      transform: none;
    }
  }
`;
</script>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { getPromptSuggestions } from '@/services/chatbot';

const props = defineProps({
  // Whether to show the prompts. The host decides, since only it knows whether the chat is
  // in a state where starting a new conversation makes sense.
  visible: {
    type: Boolean,
    default: false,
  },
  // Replaces the default description - e.g. with a message explaining why a new chat was
  // started - while still showing this page's suggestions below it as normal.
  message: {
    type: String,
    default: null,
  },
});

defineEmits(['select']);

const welcomeGreeting = "Hi, I'm Wiz! How can I help you?";
const defaultDescription =
  "I'm an AI assistant who can answer questions about QuoteWizard client accounts and " +
  'perform simple tasks.';
const description = computed(() => props.message || defaultDescription);

const route = useRoute();
const suggestions = ref([]);

// The prompt elements currently animating out.
const leavingPrompts = new Set();
let leaveFinishedResolvers = [];

// Resolves once no prompt is animating out any more, so the next page's prompts can wait for them
// rather than entering while they're still animating out and overlapping.
function waitForPromptsToLeave() {
  return new Promise(resolve => {
    leaveFinishedResolvers.push(resolve);
  });
}

// Deliberately declares only the element, not the "done" callback Vue passes alongside it: a leave
// hook taking more than one argument is Vue's signal that the hook itself will decide when the
// prompt is finished leaving, which would stop it waiting for the CSS transition to end.
function onPromptLeave(element) {
  leavingPrompts.add(element);
}

function onPromptAfterLeave(element) {
  leavingPrompts.delete(element);
  if (leavingPrompts.size > 0) {
    return;
  }
  const resolvers = leaveFinishedResolvers;
  leaveFinishedResolvers = [];
  resolvers.forEach(resolve => resolve());
}

// The API takes the route the user is on as its "page" and returns the prompts to suggest
// there, so the suggestions are refreshed on every navigation.
watch(
  () => route.name,
  async page => {
    const pageSuggestions = await getPromptSuggestions(page);
    // Ignore stale results if the user navigated again while fetching.
    if (route.name !== page) {
      return;
    }

    // A prompt suggested on both the old and new page keeps its place rather than leaving and
    // re-entering - only ones that are actually going away are removed here. Vue's keyed diffing
    // then leaves the rest alone since they never leave the array.
    const newNames = new Set(pageSuggestions.map(suggestion => suggestion.name));
    const kept = suggestions.value.filter(suggestion => newNames.has(suggestion.name));
    const leavingCount = suggestions.value.length - kept.length;

    // Let the departing prompts leave first, otherwise the new prompts enter while the old ones
    // are still fading out and the two visibly overlap.
    if (leavingCount > 0 || leavingPrompts.size > 0) {
      const leaveFinished = waitForPromptsToLeave();
      suggestions.value = kept;
      await leaveFinished;
      // Check again - navigating away again during the leave shouldn't overwrite that page's prompts.
      if (route.name !== page) {
        return;
      }
    }

    suggestions.value = pageSuggestions;
  },
  { immediate: true }
);

// The actual, unclipped height of the prompt buttons, kept in sync via ResizeObserver so
// suggested-prompts-grow (see suggestedPromptsStyle) can transition to it smoothly instead of
// snapping whenever the number of prompts changes. It's measured off promptsListRef rather than
// derived from suggestions.length because leaving prompts still occupy their full height for the
// duration of their leave transition, and this needs to reflect that too.
const promptsListRef = ref(null);
const measuredHeight = ref(0);
let resizeObserver = null;

onMounted(() => {
  const listElement = promptsListRef.value?.$el;
  if (listElement && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(([entry]) => {
      measuredHeight.value = entry.contentRect.height;
    });
    resizeObserver.observe(listElement);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});
</script>
